import axios from 'axios';

import { BFF_BASE_URL } from '../auth/authApi';

/** One remembered fact (backend facts_admin.fact_view). `valid_to` set means it is no longer true. */
export interface MemoryFact {
    id: string;
    user_id: string;
    person_id: string | null;
    person: string | null;
    category: string;
    key: string;
    value: string;
    confidence: number;
    /** When it began / ended in the user's life, as precisely as they said: "2011", "2012-03", "2012-03-04". */
    valid_from: string | null;
    valid_until: string | null;
    /** The day VictorIA closed it (replaced by a newer fact, or marked no longer true), not a day in the user's life. */
    valid_to: string | null;
    created_at: string;
    last_confirmed_at: string;
    source_msg_id: string | null;
    /** The words of the user's message the fact was taken from. */
    source_excerpt: string | null;
}

export interface MemoryPerson {
    id: string;
    user_id: string;
    name: string;
    aliases: string[];
    /** How they relate to the user, from their current relationship_to_user fact. */
    relationship: string | null;
    open: MemoryFact[];
    history: MemoryFact[];
}

export interface MemoryOverview {
    self: { open: MemoryFact[]; history: MemoryFact[] };
    people: MemoryPerson[];
    recent: MemoryFact[];
    totals: { open: number; closed: number; people: number };
}

export interface RecallPreview {
    block: string;
    chars: number;
    budget: number;
    /** MEMORY_FACTS_ENABLED on the backend: when false, replies do not get this block. */
    enabled: boolean;
    candidates: number;
    named: string[];
}

/** The fact categories (backend fact_extraction.CATEGORIES). */
export const CATEGORY_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
    identity: 'default',
    relationship: 'secondary',
    health: 'error',
    preference: 'info',
    goal: 'success',
    fear: 'warning',
    value: 'primary',
    routine: 'default',
    event: 'default',
    emotion: 'warning',
    work: 'primary',
};

const url = (path: string): string => `${BFF_BASE_URL}/admin/memory${path}`;

export const getMemory = async (userId: string): Promise<MemoryOverview> =>
    (await axios.get<MemoryOverview>(url(''), { params: { user_id: userId } })).data;

export const previewRecall = async (userId: string, message: string): Promise<RecallPreview> =>
    (await axios.get<RecallPreview>(url('/recall'), { params: { user_id: userId, message } })).data;

export const retireFact = async (factId: string): Promise<void> => {
    await axios.post(url(`/facts/${encodeURIComponent(factId)}/retire`));
};

/** What of a fact can be fixed; a key left out is left alone, and an empty date clears it. */
export interface FactChanges {
    value?: string;
    valid_from?: string;
    valid_until?: string;
}

export const correctFact = async (factId: string, changes: FactChanges): Promise<void> => {
    await axios.put(url(`/facts/${encodeURIComponent(factId)}`), changes);
};

/** A date as the user gave it: a year, a month or a day. */
export const isPartialDate = (text: string): boolean => /^\d{4}(-(0[1-9]|1[0-2])(-(0[1-9]|[12]\d|3[01]))?)?$/.test(text.trim());

export const addAlias = async (personId: string, alias: string): Promise<void> => {
    await axios.post(url(`/people/${encodeURIComponent(personId)}/aliases`), { alias });
};

/** The date part of an ISO timestamp, or an empty string. */
export const day = (iso: string | null): string => (iso ? iso.slice(0, 10) : '');

/**
 * When a fact was true, only as far as it is known: "2011 → 2012", "since 2011", "until 2012". A fact with
 * no dates says when it was noted (or replaced) instead, so it never reads as if it began that day.
 */
export const whenLabel = (fact: MemoryFact): string => {
    if (fact.valid_from && fact.valid_until) {
        return fact.valid_from === fact.valid_until ? fact.valid_from : `${fact.valid_from} → ${fact.valid_until}`;
    }
    if (fact.valid_from) {
        return fact.valid_to ? `from ${fact.valid_from}` : `since ${fact.valid_from}`;
    }
    if (fact.valid_until) {
        return `until ${fact.valid_until}`;
    }
    return fact.valid_to ? `replaced ${day(fact.valid_to)}` : `noted ${day(fact.created_at)}`;
};
